/**
 * The only module in this app that talks to a server.
 *
 * It imports NOTHING. That is a rule rather than a coincidence of today's code,
 * and it is what makes the privacy boundary provable instead of asserted: a test
 * walks this module's import graph and asserts it never reaches `Plan`,
 * `SimInputs` or `ScenarioSet`. A closure of one cannot reach anything.
 *
 * The body is rebuilt field by field rather than serialising the argument, so
 * widening the caller cannot smuggle a field through. `JSON.stringify(input)`
 * would have been shorter and would have quietly forwarded whatever it was
 * handed — which is the whole thing this file exists not to do.
 *
 * The endpoint is a same-origin relative path. It must stay relative: an
 * absolute URL here would send an address to a host nobody has disclosed, and
 * the verification greps are written to catch exactly that substitution.
 */

/** The whole shape this module will ever send. */
export interface Subscription {
  email: string
  consent: true
}

/**
 * `unavailable` is deliberately separate from `failed`, and the distinction is
 * the reader's, not the developer's.
 *
 * `failed` is a network that did not deliver — offline, a captive portal, a
 * timeout. Trying again in a moment is genuinely the right advice.
 *
 * `unavailable` is the server refusing because its store is unreachable. That
 * cannot fix itself, so telling someone to retry is false, and letting the
 * generic message imply they typed something wrong is worse — it reads as their
 * fault when it is a misconfiguration on our side. Found the hard way: a missing
 * KV binding on a preview deployment answered every signup with "That didn't
 * send. Try again in a moment."
 */
export type SubscribeOutcome = 'ok' | 'invalid' | 'unavailable' | 'failed'

export const SUBSCRIBE_ENDPOINT = '/api/subscribe'

/** Long enough for a slow phone on 3G, short enough to not read as broken. */
const TIMEOUT_MS = 10_000

/**
 * Kept byte-identical to EMAIL_PATTERN in `functions/api/subscribe.ts`, and a
 * test reads both files and compares the literals. The two directories share no
 * module, so the duplication is forced; two copies drifting apart is a failure
 * this repo has already filed twice under other names.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/
const MAX_EMAIL_LENGTH = 254

export function isValidEmail(value: string): boolean {
  // Bytes, matching the handler. RFC 5321 caps an address at 254 octets, and
  // measuring characters instead lets a multi-byte address through here and be
  // refused there — the two disagreeing about one rule, which is the bug the
  // parity test exists to prevent.
  return (
    new TextEncoder().encode(value).length <= MAX_EMAIL_LENGTH && EMAIL_PATTERN.test(value)
  )
}

export async function subscribe(input: Subscription): Promise<SubscribeOutcome> {
  let response: Response
  try {
    response = await fetch(SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // Field by field. Never the argument itself.
      body: JSON.stringify({ email: input.email, consent: input.consent }),
      /* Without this a stalled connection — a captive portal is the common one —
         leaves the promise pending forever, and the form sits on "Sending…" with
         a disabled button and no route back short of a page reload. An abort
         lands in the catch below and reads as 'failed', which is exactly right:
         the reader is told to try again. */
      signal: AbortSignal.timeout(TIMEOUT_MS),
    })
  } catch {
    // Offline, blocked, or the request never left. Indistinguishable from here
    // and it does not matter — the reader gets the same "try again" either way.
    return 'failed'
  }

  if (response.status === 202) return 'ok'
  if (response.status === 400) return 'invalid'
  if (response.status === 503) return 'unavailable'
  return 'failed'
}
